// 消息查询与详情的接口（docs/04 第 8 节）：控制面只读。
import type { MessageState } from "@/ui";
import { api } from "./api";
import type { ListCount } from "./usePage";
import type { Protocol, TrafficType } from "./master";

/** 列表与详情共有的消息字段，同 message 表（docs/03 5.1）；error_name 是 docs/05 的短名。 */
export interface MessageFields {
  id: string; created_at: string; account_id: number; customer_id: number; protocol: Protocol; status: MessageState; traffic_type: TrafficType;
  source_addr: string; dest_msisdn: string; dest_mcc: string | null; dest_mnc: string | null; segments: number; error_code: string | null; error_name: string;
  routed_at: string | null; submitted_at: string | null; done_at: string | null; customer_dlr_status: string | null; customer_dlr_final_at: string | null; client_ref: string | null;
}

/** 尝试的状态（docs/03 5.4）。 */
export type AttemptStatus = "PENDING" | "SUBMITTED" | "DELIVERED" | "FAILED";

/** 列表不聚合重投时的一次尝试（决策 D187）。 */
export interface MessageAttempt {
  id: string; seq_no: number; channel_id: number; status: AttemptStatus; submitted_at: string | null; done_at: string | null;
  upstream_error: string; failure_reason: string; dlr_error: string;
}

/** 列表里的一条消息：另带尝试次数与最新一次尝试的通道，还没开尝试时通道为空（D62，#204）；不聚合重投时带全部尝试，序号从大到小（决策 D187）。 */
export interface MessageRow extends MessageFields { attempt_seq: number; channel_id: number | null; attempts?: MessageAttempt[] }

/** 价格快照（架构说明第 10 节）。 */
export interface PriceSnapshot { charge_on?: "submitted" | "delivered"; price_id: number; price: number; currency: string; rate: number; base_total: number }

export interface MessageDetail extends MessageFields {
  /** 首发实际无路由的历史诊断；旧接口或旧消息可能没有记录（D128）。 */
  route_failure_trace?: unknown;
  encoding: string; text: string; part_count: number; registered_delivery: boolean; validity_period: string | null;
  route_plan: unknown; sell: PriceSnapshot | null; hold_amount: number; billing_settled_at: string | null;
  failure_reason: string; customer_dlr_error: string;
 customer_dlr_reason?: string | null; customer_dlr_deadline?: string | null;
 customer_dlr_policy?: { id: number; version: number; timeout_sec: number; failure_success_bps: number } | null;
  /** 后台发送测试的消息（决策 D94）。 */
  test: boolean;
}

/** 上游侧的一段（docs/03 5.5）。 */
export interface AttemptPart {
  seq: number; channel_id: number; upstream_id: string; submit_category: string; upstream_status_code: string; submitted_at: string | null;
  dlr_status: string; dlr_error: string; dlr_done_at: string | null; dlr_at: string | null; dlr_count: number;
}

/** 一次尝试的 SID 替换记录（决策 D154）：命中的策略与规则；replaced 为假时原样发送（策略命中但规则没命中，或正则展开后不合法、原因在 error）。 */
export interface SenderRewrite {
  policy_id: number; policy_version: number; rule?: "mapping" | "pattern" | "default"; index?: number;
  from: string; replaced: boolean; to: string; ton: number; npi: number; error?: string;
}

/** 一次提交（docs/03 5.4）：发给通道的内容、成本快照与路由 trace。 */
export interface Attempt {
  id: string; created_at: string; channel_id: number; supplier_id: number; seq_no: number; status: AttemptStatus; submit_count: number; last_submit_at: string | null;
  rendered_sender: string; sender_rewrite: SenderRewrite | null; rendered_encoding: string; rendered_text: string; segments: number; upstream_segments: number | null; cost: PriceSnapshot | null;
  result_category: string; upstream_error: string; failure_reason: string; route_trace: unknown;
  submitted_at: string | null; dlr_status: string; dlr_error: string; dlr_at: string | null; done_at: string | null; parts: AttemptPart[];
}

export interface MessageDetailResponse { message: MessageDetail; attempts: Attempt[] }

/** 列表与计数的条件（docs/04 第 8 节）；per_attempt 为 "true" 时每条消息带全部尝试，只影响列表（决策 D187）。 */
export interface MessageFilter {
  message_id?: string; msisdn?: string; account_id?: string; customer_id?: string; channel_id?: string; final_channel_id?: string; min_attempts?: string;
  mcc?: string; mnc?: string; status?: string; per_attempt?: string; from?: string; to?: string; before_id?: string; limit?: number;
}

/** 下一页的起点：to 之前，或 created_at 等于 to 且 id 比 before_id 小（决策 D82）。 */
export interface MessageCursor { to: string; before_id?: string }

/** 列表的一页；next 为空表示时间范围已经查完，不为空时这一页可能不满（服务端用完了扫描预算）。 */
export interface MessagePage { rows: MessageRow[]; next: MessageCursor | null }

/** 网关判定的终态写成运营看得懂的说法，原始值放括号里；其余失败原因原样显示（决策 D54 第 4、5 条、D185）。详情与不聚合的列表共用。 */
export function attemptVerdict(a: Pick<Attempt, "failure_reason" | "dlr_error">) {
  if (a.failure_reason === "dlr_timeout") return "回执超时（dlr_timeout）";
  if (a.failure_reason === "superseded") return "已作废，未发出（superseded）";
  if (a.dlr_error === "no_dlr") return "推定送达（no_dlr）";
  return a.failure_reason;
}

/** 不是最新一次又没到终态的尝试：超时后被新尝试取代、再没等到回执，停在原状态但不再影响消息（D62 第 9 条）。 */
export function superseded(a: Pick<Attempt, "seq_no" | "status">, latest: number) {
  return a.seq_no < latest && (a.status === "PENDING" || a.status === "SUBMITTED");
}

/** 尝试状态借用消息状态胶囊：待提交的尝试显示“已路由”，其余同名（决策 D187）。 */
export const ATTEMPT_PILL: Record<AttemptStatus, MessageState> = { PENDING: "ROUTED", SUBMITTED: "SUBMITTED", DELIVERED: "DELIVERED", FAILED: "FAILED" };

/** 列表的一行：一条消息（聚合重投），或它的一次尝试（不聚合，决策 D187）；top 表示这条消息显示出来的行里序号最大的一行，消息级的客户回执写在这行。 */
export interface MessageLine { m: MessageRow; a?: MessageAttempt; top: boolean }

/** 把一页消息展开成行：聚合时一条消息一行；不聚合时每次尝试一行（序号从大到小），按通道筛时只留发到这个通道的尝试；还没开尝试的消息仍占一行。 */
export function messageLines(rows: MessageRow[], perAttempt: boolean, channelId: string): MessageLine[] {
  if (!perAttempt) return rows.map((m) => ({ m, top: true }));
  return rows.flatMap((m): MessageLine[] => {
    const list = (m.attempts ?? []).filter((a) => !channelId || String(a.channel_id) === channelId);
    return list.length ? list.map((a, i) => ({ m, a, top: i === 0 })) : [{ m, top: true }];
  });
}

/** 查询条件转成查询串，空值不带；导出任务的条件同样放在查询串里（决策 D202）。 */
export function messageParams(f: MessageFilter) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== "") q.set(k, String(v));
  return q;
}

export const messages = {
  list: (f: MessageFilter, signal?: AbortSignal) => api<MessagePage>(`/messages?${messageParams(f)}`, { signal }),
  /** 范围内匹配条件的总数：封顶 10,000 条、限时 2 秒，超时 count 为空（决策 D182）；只按条件取，不带翻页参数。 */
  count: (f: MessageFilter, signal?: AbortSignal) => api<ListCount>(`/messages/count?${messageParams(f)}`, { signal }),
  get: (id: string) => api<MessageDetailResponse>(`/messages/${id}`),
};
